import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { WorkspaceCollaboratorProfile } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { useProjectMapComments } from "features/comments/Comments"
import { MapCommentSort } from "plugins/Toolbox/MapComments/CommentSlice"

const EVERYONE = "everyone"

const maxVisibleCreators = 5
const creatorRowHeight = 40
const creatorRowGap = 4
const creatorMenuPadding = 16
const creatorMenuMaxHeight =
    maxVisibleCreators * creatorRowHeight +
    (maxVisibleCreators - 1) * creatorRowGap +
    creatorMenuPadding

const CreatorDropdown = ({
    creatorId,
    onCreatorChange,
}: {
    creatorId: string
    onCreatorChange: (creatorId: string) => void
}) => {
    const members = useAppSelector(selectWorkspaceMembers)
    const { comments } = useProjectMapComments()
    const selectedName = members.find((member) => member.userId === creatorId)?.fullName

    const commenterIds = new Set((comments ?? []).map((comment) => comment.createdBy))
    const commenters = members.filter((member) => commenterIds.has(member.userId))

    return (
        <Dropdown>
            <Dropdown.Trigger className="w-full justify-between rounded-lg border border-white-100 bg-gray-900 px-4 py-1.5 text-gray-0 text-sm">
                <span>{creatorId === EVERYONE ? "Everyone" : (selectedName ?? "Everyone")}</span>
                <Icons.ExtraSmallIcon Icon={Icons.ArrowDropdown} className="text-gray-300" />
            </Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                matchTriggerWidth
                className="flex flex-col gap-1 overflow-y-auto rounded-lg border border-white-200 bg-black-900 py-2 shadow-dp4"
                style={{ maxHeight: creatorMenuMaxHeight }}
            >
                <Dropdown.ButtonItem
                    className="h-10 shrink-0 gap-3 px-3 py-0.5 hover:bg-white-200"
                    onClick={() => onCreatorChange(EVERYONE)}
                >
                    <span className="grow text-left text-white text-xs">Everyone</span>
                    {creatorId === EVERYONE && (
                        <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                    )}
                </Dropdown.ButtonItem>
                {commenters.map((member) => (
                    <Dropdown.ButtonItem
                        key={member.userId}
                        className="h-10 shrink-0 gap-3 px-3 py-0.5 hover:bg-white-200"
                        onClick={() => onCreatorChange(member.userId)}
                    >
                        <WorkspaceCollaboratorProfile
                            userId={member.userId}
                            fullName={member.fullName}
                            email={member.email}
                            isFullWidth
                        />
                        {creatorId === member.userId && (
                            <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                        )}
                    </Dropdown.ButtonItem>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    )
}

const sortLabels = {
    date: "Date",
    unread: "Unread",
} satisfies Record<MapCommentSort, string>

export const MapCommentFilterMenu = ({
    creatorId,
    onCreatorChange,
    sort,
    onSortChange,
    children,
}: {
    creatorId: string
    onCreatorChange: (creatorId: string) => void
    sort: MapCommentSort
    onSortChange: (sort: MapCommentSort) => void
    children: React.ReactNode
}) => {
    return (
        <Dropdown>
            <Dropdown.Trigger as="div">{children}</Dropdown.Trigger>
            <Dropdown.Menu position="bottom-right" className="flex w-[248px] flex-col gap-3 py-2">
                <div className="flex flex-col">
                    <small className="px-3 pl-11 text-gray-300 uppercase tracking-[1.2px]">
                        Sort By
                    </small>
                    {Object.entries(sortLabels).map(([value, label]) => (
                        <Dropdown.ButtonItem
                            key={value}
                            className="gap-3 px-3 py-1.5 hover:bg-white-200"
                            onClick={() => onSortChange(value as MapCommentSort)}
                        >
                            <div className="h-5 w-5 shrink-0">
                                {sort === value && (
                                    <Icons.SmallIcon
                                        Icon={Icons.Checkmark}
                                        className="text-green-500"
                                    />
                                )}
                            </div>
                            <span className="grow text-left text-gray-0 text-sm">{label}</span>
                        </Dropdown.ButtonItem>
                    ))}
                </div>
                <div className="h-px w-full bg-white-200" />
                <div className="flex flex-col gap-1 px-3 pb-1">
                    <small className="text-gray-100">Creator</small>
                    <CreatorDropdown creatorId={creatorId} onCreatorChange={onCreatorChange} />
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}

export { EVERYONE as EVERYONE_CREATOR }
